// IndicatorsView — vista ejecutiva con Chart.js.
//
// Tres gráficas:
//   1. Doughnut: distribución global por estado.
//   2. Bar (% por fase): porcentaje aprobado por fase 0-6.
//   3. Bar horizontal: documentos por audiencia.
//
// Tanda 6. Chart.js v4 con auto-registro.

import { html } from 'htm/preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { Chart } from 'chart.js/auto'

import {
  initInventory,
  getInventoryState,
  subscribeInventoryChange
} from '../lib/inventory-store.js'
import {
  initValidation,
  subscribeValidation
} from '../lib/validation-store.js'
import {
  countsByEstado,
  percentByFase,
  countsByAudiencia
} from '../lib/client-aggregator.js'

// Paleta JMA aplicada a los datasets para coherencia con dots/badges.
const ESTADO_COLOR = {
  Aprobado: '#80C030',
  Pendiente: '#5B9BD5',
  'Requiere ajuste': '#D68910',
  Bloqueado: '#B22222',
  Obsoleto: '#9C9C9C',
  'Sin estado': '#D0D5DB'
}

const CHART_FONT = { family: 'Arial', size: 12 }

function useChart(buildConfig, depKey) {
  const canvasRef = useRef(null)
  const chartRef = useRef(null)
  useEffect(() => {
    if (!canvasRef.current) return
    const config = buildConfig()
    if (chartRef.current) {
      chartRef.current.destroy()
      chartRef.current = null
    }
    chartRef.current = new Chart(canvasRef.current, config)
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey])
  return canvasRef
}

function StatusDoughnut({ counts }) {
  const labels = Object.keys(ESTADO_COLOR)
  const data = labels.map((l) => counts[l] || 0)
  const colors = labels.map((l) => ESTADO_COLOR[l])
  const depKey = JSON.stringify(counts)
  const ref = useChart(
    () => ({
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderColor: '#FFFFFF',
            borderWidth: 2,
            hoverOffset: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '55%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { font: CHART_FONT, color: '#595959', boxWidth: 12 }
          },
          tooltip: {
            titleFont: CHART_FONT,
            bodyFont: CHART_FONT,
            backgroundColor: '#1A1A1A'
          }
        }
      }
    }),
    depKey
  )
  return html`<canvas ref=${ref}></canvas>`
}

function PercentByFaseChart({ porFase }) {
  const labels = Object.keys(porFase)
  const data = labels.map((k) => porFase[k].porcentaje)
  const totales = labels.map((k) => porFase[k].total)
  const depKey = JSON.stringify(porFase)
  const ref = useChart(
    () => ({
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: '% Aprobado',
            data,
            backgroundColor: '#105070',
            borderRadius: 4,
            barThickness: 22
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            titleFont: CHART_FONT,
            bodyFont: CHART_FONT,
            backgroundColor: '#1A1A1A',
            callbacks: {
              label: (ctx) => {
                const idx = ctx.dataIndex
                return `${ctx.parsed.y}% aprobado (${totales[idx]} doc${totales[idx] === 1 ? '' : 's'})`
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: (v) => `${v}%`,
              font: CHART_FONT,
              color: '#595959'
            },
            grid: { color: '#F2F4F6' }
          },
          x: {
            ticks: { font: CHART_FONT, color: '#595959' },
            grid: { display: false }
          }
        }
      }
    }),
    depKey
  )
  return html`<canvas ref=${ref}></canvas>`
}

function AudienciaChart({ porAudiencia }) {
  const labels = Object.keys(porAudiencia)
  const data = labels.map((k) => porAudiencia[k])
  const depKey = JSON.stringify(porAudiencia)
  const ref = useChart(
    () => ({
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Documentos',
            data,
            backgroundColor: '#005070',
            borderRadius: 4
          }
        ]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            titleFont: CHART_FONT,
            bodyFont: CHART_FONT,
            backgroundColor: '#1A1A1A'
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { font: CHART_FONT, color: '#595959', stepSize: 10 },
            grid: { color: '#F2F4F6' }
          },
          y: {
            ticks: { font: CHART_FONT, color: '#595959' },
            grid: { display: false }
          }
        }
      }
    }),
    depKey
  )
  return html`<canvas ref=${ref}></canvas>`
}

function ChartCard({ title, hint, children, wide }) {
  return html`
    <article class=${`chart-card ${wide ? 'chart-card--wide' : ''}`}>
      <header class="chart-card__head">
        <h3 class="chart-card__title">${title}</h3>
        ${hint ? html`<p class="chart-card__hint">${hint}</p>` : null}
      </header>
      <div class="chart-card__canvas-wrapper">${children}</div>
    </article>
  `
}

export function IndicatorsView() {
  const [inv, setInv] = useState(getInventoryState())
  const [, forceRender] = useState(0)

  useEffect(() => {
    let cancelled = false
    const unsubInv = subscribeInventoryChange((s) => {
      if (!cancelled) setInv(s)
    })
    const unsubVal = subscribeValidation(() => {
      if (!cancelled) forceRender((v) => v + 1)
    })
    initInventory().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[IndicatorsView] initInventory falló:', err)
    })
    initValidation().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[IndicatorsView] initValidation falló:', err)
    })
    return () => {
      cancelled = true
      unsubInv()
      unsubVal()
    }
  }, [])

  const files = inv.data?.files || []

  const counts = useMemo(() => countsByEstado(files), [files, inv])
  const porFase = useMemo(() => percentByFase(files), [files, inv])
  const porAudiencia = useMemo(() => countsByAudiencia(files), [files, inv])

  if ((inv.state === 'loading' || inv.state === 'idle') && !inv.data) {
    return html`
      <div class="indicators-view">
        <section class="section">
          <h1 class="section__title">Indicadores</h1>
          <p class="section__subtitle">Cargando datos del inventario…</p>
        </section>
      </div>
    `
  }

  const total = files.length
  const conEstado = total - (counts['Sin estado'] || 0)

  return html`
    <div class="indicators-view">
      <section class="section">
        <h1 class="section__title">Indicadores ejecutivos</h1>
        <p class="section__subtitle">
          Vista de gobernanza con distribución de estados, avance por
          fase y composición del inventario. Datos sincronizados con
          SharePoint y validacion.json.
        </p>
      </section>

      <section class="section">
        <div class="kpi-grid">
          <article class="kpi-card">
            <div class="kpi-card__label">Total de documentos</div>
            <div class="kpi-card__value">${total}</div>
            <div class="kpi-card__hint">Inventario completo</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">Con estado registrado</div>
            <div class="kpi-card__value">${conEstado}</div>
            <div class="kpi-card__hint">${total > 0 ? `${Math.round((conEstado / total) * 100)}% del total` : '—'}</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">Aprobados</div>
            <div class="kpi-card__value">${counts['Aprobado']}</div>
            <div class="kpi-card__hint">${conEstado > 0 ? `${Math.round((counts['Aprobado'] / conEstado) * 100)}% de los validados` : '—'}</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">Requieren ajuste</div>
            <div class="kpi-card__value">${counts['Requiere ajuste']}</div>
            <div class="kpi-card__hint">Pendientes para Cowork</div>
          </article>
        </div>
      </section>

      <section class="section">
        <h2>Gráficas</h2>
        <div class="chart-grid">
          <${ChartCard}
            title="Distribución global por estado"
            hint="Suma sobre los 107 documentos del proyecto."
          >
            <${StatusDoughnut} counts=${counts} />
          <//>
          <${ChartCard}
            title="% Aprobado por fase"
            hint="Porcentaje de documentos aprobados sobre el total de cada fase."
          >
            <${PercentByFaseChart} porFase=${porFase} />
          <//>
          <${ChartCard}
            title="Documentos por audiencia"
            hint="Composición del inventario por destinatario documental."
            wide=${true}
          >
            <${AudienciaChart} porAudiencia=${porAudiencia} />
          <//>
        </div>
      </section>
    </div>
  `
}
